import * as piexif from 'piexif-ts';
import type { IExif } from 'piexif-ts';
import type { PhotoMetadata } from '@transpordiamet/shared';

const TagValues = piexif.TagValues;

/** Bytes per String.fromCharCode call — a whole multi-MB photo blows the stack. */
const CHUNK_SIZE = 0x8000;

/**
 * Convert decimal GPS coordinates to Degrees/Minutes/Seconds format
 * @param decimal - Decimal coordinate (e.g., 59.4375)
 * @returns [degrees, minutes, seconds] array (e.g., [59, 26, 15])
 */
function decimalToDMS(decimal: number): [number, number, number] {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutesDecimal = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesDecimal);
  const seconds = (minutesDecimal - minutes) * 60;

  return [degrees, minutes, seconds];
}

/**
 * Format JavaScript Date to EXIF DateTime format
 * @param date - JavaScript Date object
 * @returns EXIF formatted string "YYYY:MM:DD HH:MM:SS"
 */
function formatDateTimeForExif(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}:${month}:${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Build piexif-ts compatible EXIF data structure
 * @param metadata - GPS and timestamp metadata
 * @returns piexif IFD dictionary object
 */
export function buildPiexifExifData(metadata: PhotoMetadata): IExif {
  const { latitude, longitude, capturedAt } = metadata;
  const dateTimeString = formatDateTimeForExif(new Date(capturedAt));

  const exif: IExif = {
    '0th': {
      [TagValues.ImageIFD.Make]: 'Transpordiamet',
      [TagValues.ImageIFD.Software]: 'Vehicle Inspection System v1.0',
      [TagValues.ImageIFD.DateTime]: dateTimeString,
    },
    Exif: {
      [TagValues.ExifIFD.DateTimeOriginal]: dateTimeString,
      [TagValues.ExifIFD.DateTimeDigitized]: dateTimeString,
    },
  };
  if (latitude == null || longitude == null) return exif;

  // Convert decimal coordinates to DMS
  const [latDeg, latMin, latSec] = decimalToDMS(latitude);
  const [lonDeg, lonMin, lonSec] = decimalToDMS(longitude);

  return {
    ...exif,
    GPS: {
      [TagValues.GPSIFD.GPSVersionID]: [2, 3, 0, 0], // GPS version 2.3.0.0
      [TagValues.GPSIFD.GPSLatitudeRef]: latitude >= 0 ? 'N' : 'S',
      [TagValues.GPSIFD.GPSLatitude]: [
        [latDeg, 1],
        [latMin, 1],
        [Math.round(latSec * 1000), 1000], // Store seconds with 3 decimal precision
      ],
      [TagValues.GPSIFD.GPSLongitudeRef]: longitude >= 0 ? 'E' : 'W',
      [TagValues.GPSIFD.GPSLongitude]: [
        [lonDeg, 1],
        [lonMin, 1],
        [Math.round(lonSec * 1000), 1000], // Store seconds with 3 decimal precision
      ],
    },
  };
}

/**
 * piexif works on a "binary string" — one character per byte. Map the bytes by
 * hand: TextDecoder('latin1') is an alias for windows-1252, which rewrites
 * 0x80–0x9F (0x80 → U+20AC) and would corrupt the image data on the way back.
 */
async function blobToJpegBinary(blob: Blob): Promise<string> {
  const buffer =
    typeof blob.arrayBuffer === 'function'
      ? await blob.arrayBuffer()
      : await new Promise<ArrayBuffer>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.onerror = () => reject(reader.error ?? new Error('Failed to read photo'));
          reader.readAsArrayBuffer(blob);
        });

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK_SIZE));
  }
  return binary;
}

function jpegBinaryToBlob(binary: string): Blob {
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: 'image/jpeg' });
}

/**
 * Embed EXIF data into a JPEG blob.
 *
 * Works on the raw bytes rather than a base64 data URL: the camera still is
 * several MB and base64 round-tripping it is seconds of work on a small phone.
 * Replacing APP1 also drops the camera's Orientation tag, so the stored JPEG
 * holds the same sensor pixels the viewfinder and the preview show.
 *
 * @param jpegBlob - Original JPEG blob from the camera
 * @param metadata - GPS and timestamp metadata to embed
 * @returns Promise resolving to new JPEG blob with embedded EXIF data
 */
export async function embedExifIntoJpeg(
  jpegBlob: Blob,
  metadata: PhotoMetadata
): Promise<{ blob: Blob; exifData: IExif | null }> {
  try {
    const binary = await blobToJpegBinary(jpegBlob);
    const exifData = buildPiexifExifData(metadata);
    const blobWithExif = jpegBinaryToBlob(piexif.insert(piexif.dump(exifData), binary));

    console.log('EXIF data successfully embedded into JPEG', {
      originalSize: jpegBlob.size,
      newSize: blobWithExif.size,
      metadata,
    });

    return { blob: blobWithExif, exifData };
  } catch (error) {
    console.error('Failed to embed EXIF data into JPEG:', error);
    // Return original blob as fallback
    return { blob: jpegBlob, exifData: null };
  }
}
